import { useCallback, useEffect, useRef } from "react";

/**
 * Put a scrolled list back where the reader left it.
 *
 * Opening a case and pressing back re-mounts the list at the top, which on a
 * long list means finding your place again every time. This remembers the
 * offset per key and restores it once the rows are back.
 *
 * sessionStorage rather than a module variable: it survives a full reload and
 * the browser's own back button, and it is scoped to the tab, so two tabs on
 * the same list do not fight over one position.
 *
 * What it deliberately does NOT do is reload pages. With infinite scroll a
 * reader can be 400 rows deep, and coming back re-fetches only the first
 * page — so the offset is clamped to what actually exists. You land as far
 * down as the list currently goes, not at the top.
 */

const KEY_PREFIX = "scroll:";

const storage = () => {
  try {
    return window.sessionStorage;
  } catch {
    // Private mode, or storage disabled. Scroll memory is a convenience.
    return null;
  }
};

export const readOffset = (key) => {
  const store = storage();
  if (!store || !key) return 0;
  const raw = Number(store.getItem(`${KEY_PREFIX}${key}`));
  return Number.isFinite(raw) && raw > 0 ? raw : 0;
};

export const writeOffset = (key, offset) => {
  const store = storage();
  if (!store || !key) return;
  try {
    if (offset > 0) store.setItem(`${KEY_PREFIX}${key}`, String(Math.round(offset)));
    else store.removeItem(`${KEY_PREFIX}${key}`);
  } catch {
    /* quota, or storage disabled mid-session */
  }
};

/** Never scroll past the end: the list may be shorter than it was. */
export const clampOffset = (offset, element) => {
  if (!element) return 0;
  const max = Math.max(0, element.scrollHeight - element.clientHeight);
  return Math.min(Math.max(0, offset), max);
};

/**
 * @param elementId  the scroll container's id
 * @param key        what the position belongs to — include the client and the
 *                   route, so one list does not restore another's offset
 * @param ready      false while the list is still loading; the restore waits
 */
export const useScrollMemory = ({ elementId, key, ready }) => {
  const restored = useRef(false);
  const latest = useRef(0);

  // Remember as the reader scrolls. Cheap: one number, written on a frame.
  useEffect(() => {
    const element = document.getElementById(elementId);
    if (!element) return undefined;

    let frame = 0;
    const onScroll = () => {
      latest.current = element.scrollTop;
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        writeOffset(key, latest.current);
      });
    };

    element.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      element.removeEventListener("scroll", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
      // The last position, even if the final frame never ran.
      if (latest.current > 0) writeOffset(key, latest.current);
    };
  }, [elementId, key, ready]);

  /**
   * Restore once, after the rows exist AND the container can actually scroll.
   *
   * Waiting a single frame was not enough. When the rows come from a cache
   * they are set synchronously on mount, so `ready` flips in the same commit
   * and that frame runs before layout: the container still measures zero,
   * clampOffset returns 0, and marking the restore done locks the reader at
   * the top for good. It only ever worked because a network round trip
   * happened to put the rows in after paint.
   *
   * So: keep asking until the element has something to scroll, then restore.
   * Bounded, because a genuinely short list never becomes scrollable and this
   * must not spin for the life of the page.
   */
  useEffect(() => {
    if (!ready || restored.current) return undefined;

    const wanted = readOffset(key);
    if (!wanted) {
      restored.current = true;
      return undefined;
    }

    let frame = 0;
    let attemptsLeft = 60; // ~1s at 60fps, then give up and take what is there

    const attempt = () => {
      const element = document.getElementById(elementId);
      if (!element) {
        frame = window.requestAnimationFrame(attempt);
        return;
      }

      // Keep applying it until it holds.
      //
      // The container becomes scrollable in stages — rows lay out, then
      // thumbnails resolve — so a single write lands on whatever the list was
      // tall enough for at that instant, and a list restored from cache is
      // short for exactly one frame. Re-applying costs nothing once it has
      // landed, and is the difference between the reader's place and the top.
      element.scrollTop = clampOffset(wanted, element);

      if (Math.abs(element.scrollTop - wanted) <= 2 || attemptsLeft <= 0) {
        restored.current = true;
        return;
      }

      attemptsLeft -= 1;
      frame = window.requestAnimationFrame(attempt);
    };

    frame = window.requestAnimationFrame(attempt);
    return () => window.cancelAnimationFrame(frame);
  }, [elementId, key, ready]);
};

export default useScrollMemory;
